import { useState, useEffect, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useBudgetCalculations } from '@/hooks/useBudgetCalculations';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { Plus, Trash2, Files, AlertCircle, Calendar, Users, ArrowDownUp } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import type { ProjectType } from '@/types';

interface EditSalesBudgetFormProps {
  projectType: ProjectType;
  projectId: number;
  salesBudget?: any;
  onChange: (changes: any) => void;
}

// Helper function to format currency
const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('en-GB', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

// Helper function to parse currency input
const parseCurrency = (value: string): number => {
  const cleaned = value.replace(/,/g, '');
  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? 0 : parsed;
};

// Number input component with formatting
const NumberInput = ({
  value,
  onChange,
  onFocus,
  onBlur,
  disabled = false,
  className = '',
  placeholder = '',
  maxValue = 999999999.99,
}: {
  value: number;
  onChange: (value: number) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  disabled?: boolean;
  className?: string;
  placeholder?: string;
  maxValue?: number;
}) => {
  const [displayValue, setDisplayValue] = useState(formatCurrency(value));
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    if (!isFocused) {
      setDisplayValue(formatCurrency(value));
    }
  }, [value, isFocused]);

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(true);
    e.target.select();
    onFocus?.();
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);
    const numValue = parseCurrency(e.target.value);
    const clampedValue = Math.min(numValue, maxValue);
    const roundedValue = Math.round(clampedValue * 100) / 100;
    onChange(roundedValue);
    setDisplayValue(formatCurrency(roundedValue));
    onBlur?.();
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDisplayValue(e.target.value);
  };

  return (
    <Input
      type="text"
      value={displayValue}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      disabled={disabled}
      className={className}
      placeholder={placeholder}
    />
  );
};

export function EditSalesBudgetForm({
  projectType,
  projectId,
  salesBudget = {},
  onChange,
}: EditSalesBudgetFormProps) {
  // Ensure salesBudget is an object
  const safeSalesBudget = salesBudget || {};

  // Get rate calculations from Rate Card
  const { roleBreakdown } = useBudgetCalculations(String(projectId), null);

  // Create a map of role names to their bill rates from Rate Card
  const roleRateMap = useMemo(() => {
    const map = new Map<string, number>();
    roleBreakdown.forEach(role => {
      map.set(role.roleName, role.averageBillRate);
    });
    console.log('[EditSalesBudgetForm] Role rate map created:', Array.from(map.entries()));
    return map;
  }, [roleBreakdown]);
  
  // Fixed Price state
  const [contractValue, setContractValue] = useState(safeSalesBudget?.contractValue ?? 0);
  const [milestones, setMilestones] = useState(() => {
    if (safeSalesBudget?.milestones && Array.isArray(safeSalesBudget.milestones) && safeSalesBudget.milestones.length > 0) {
      return safeSalesBudget.milestones.map((m: any) => ({
        ...m,
        dueDate: m.dueDate ? m.dueDate.split('T')[0] : '',
      }));
    }
    return [{ id: '1', name: 'Initial Payment', amount: 0, dueDate: '' }];
  });
  const [selectedMilestones, setSelectedMilestones] = useState<Set<string>>(new Set());

  // Time & Materials state
  const [estimatedRevenue, setEstimatedRevenue] = useState(safeSalesBudget?.estimatedRevenue ?? 0);
  const [rateCards, setRateCards] = useState(() => {
    // Check both rateCard (singular from API) and rateCards (plural for backwards compatibility)
    const cards = (safeSalesBudget as any)?.rateCards || safeSalesBudget?.rateCard;
    if (cards && Array.isArray(cards) && cards.length > 0) {
      return cards;
    }
    return [{ id: '1', role: '', billRate: 0, estimatedHours: 0 }];
  });
  const [selectedRateCards, setSelectedRateCards] = useState<Set<string>>(new Set());

  // Retainer state
  const [monthlyRetainer, setMonthlyRetainer] = useState(safeSalesBudget?.monthlyRetainer ?? 0);
  const [includedHours, setIncludedHours] = useState(safeSalesBudget?.includedHours ?? 0);
  const [overageRate, setOverageRate] = useState(safeSalesBudget?.overageRate ?? 0);

  // Delete validation dialog state
  const [showDeleteValidationDialog, setShowDeleteValidationDialog] = useState(false);
  const [invoicedMilestoneNames, setInvoicedMilestoneNames] = useState<string>('');

  // Selection handlers
  const toggleMilestoneSelection = (id: string) => {
    const newSelection = new Set(selectedMilestones);
    if (newSelection.has(id)) {
      newSelection.delete(id);
    } else {
      newSelection.add(id);
    }
    setSelectedMilestones(newSelection);
  };

  const toggleAllMilestones = () => {
    if (selectedMilestones.size === milestones.length) {
      setSelectedMilestones(new Set());
    } else {
      setSelectedMilestones(new Set(milestones.map((m: any) => m.id)));
    }
  };

  const toggleRateCardSelection = (id: string) => {
    const newSelection = new Set(selectedRateCards);
    if (newSelection.has(id)) {
      newSelection.delete(id);
    } else {
      newSelection.add(id);
    }
    setSelectedRateCards(newSelection);
  };

  const toggleAllRateCards = () => {
    if (selectedRateCards.size === rateCards.length) {
      setSelectedRateCards(new Set());
    } else {
      setSelectedRateCards(new Set(rateCards.map((r: any) => r.id)));
    }
  };

  // Helper function to get bill rate from Rate Card
  const getRoleBillRate = (roleName: string, fallbackRate: number = 0): number => {
    const rateFromCard = roleRateMap.get(roleName);
    if (rateFromCard !== undefined) {
      console.log('[EditSalesBudgetForm] Using Rate Card rate for role:', roleName, '=', rateFromCard);
      return rateFromCard;
    }
    console.warn('[EditSalesBudgetForm] No rate found in Rate Card for role:', roleName, 'using fallback:', fallbackRate);
    return fallbackRate;
  };

  // Fixed Price handlers
  const addMilestone = () => {
    const newMilestone = {
      id: -Date.now(),
      name: '',
      amount: 0,
      dueDate: '',
    };
    setMilestones([...milestones, newMilestone]);
  };

  const updateMilestone = (id: string, field: string, value: any) => {
    setMilestones(
      milestones.map((m: any) =>
        m.id === id ? { ...m, [field]: value } : m
      )
    );
  };

  const deleteMilestones = () => {
    if (selectedMilestones.size === 0) return;
    
    // Check if any selected milestones have been invoiced
    const invoicedMilestones = milestones.filter(
      (m: any) => selectedMilestones.has(m.id) && m.invoiced
    );
    
    if (invoicedMilestones.length > 0) {
      const milestoneNames = invoicedMilestones.map((m: any) => m.name || 'Unnamed').join(', ');
      setInvoicedMilestoneNames(milestoneNames);
      setShowDeleteValidationDialog(true);
      return;
    }
    
    setMilestones(milestones.filter((m: any) => !selectedMilestones.has(m.id)));
    setSelectedMilestones(new Set());
  };

  const duplicateMilestones = () => {
    if (selectedMilestones.size === 0) return;
    
    const newMilestones = [...milestones];
    const selectedIds = Array.from(selectedMilestones);
    
    for (let i = newMilestones.length - 1; i >= 0; i--) {
      const milestone = newMilestones[i];
      if (selectedIds.includes(milestone.id)) {
        const duplicated = {
          ...milestone,
          id: `milestone-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          name: `${milestone.name} (Copy)`,
        };
        newMilestones.splice(i + 1, 0, duplicated);
      }
    }
    
    setMilestones(newMilestones);
    setSelectedMilestones(new Set());
  };

  // Time & Materials handlers
  const addRateCard = () => {
    const newRateCard = {
      id: `rate-${Date.now()}`,
      role: '',
      billRate: 0,
      estimatedHours: 0,
    };
    setRateCards([...rateCards, newRateCard]);
  };

  const updateRateCard = (id: string, field: string, value: any) => {
    setRateCards(
      rateCards.map((r: any) =>
        r.id === id ? { ...r, [field]: value } : r
      )
    );
  };

  const deleteRateCards = () => {
    if (selectedRateCards.size === 0) return;
    setRateCards(rateCards.filter((r: any) => !selectedRateCards.has(r.id)));
    setSelectedRateCards(new Set());
  };

  const duplicateRateCards = () => {
    if (selectedRateCards.size === 0) return;
    
    const newRateCards = [...rateCards];
    const selectedIds = Array.from(selectedRateCards);
    
    for (let i = newRateCards.length - 1; i >= 0; i--) {
      const rateCard = newRateCards[i];
      if (selectedIds.includes(rateCard.id)) {
        const duplicated = {
          ...rateCard,
          id: `rate-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          role: `${rateCard.role} (Copy)`,
        };
        newRateCards.splice(i + 1, 0, duplicated);
      }
    }
    
    setRateCards(newRateCards);
    setSelectedRateCards(new Set());
  };

  // Calculations
  const milestonesTotal = milestones.reduce((sum: number, m: any) => sum + m.amount, 0);
  const rateCardsTotal = rateCards.reduce((sum: number, r: any) => {
    const billRate = getRoleBillRate(r.role, r.billRate);
    return sum + (billRate * r.estimatedHours);
  }, 0);
  const totalEstimatedHours = rateCards.reduce((sum: number, r: any) => sum + r.estimatedHours, 0);

  // Validation
  const hasValidationWarning = projectType === 'fixed-price' && contractValue > 0 && milestonesTotal !== contractValue;

  // Notify parent of changes
  useEffect(() => {
    if (projectType === 'fixed-price') {
      onChange({
        ...safeSalesBudget,
        projectType,
        contractValue,
        milestones,
      });
    } else if (projectType === 'time-and-materials') {
      onChange({
        ...safeSalesBudget,
        projectType,
        estimatedRevenue,
        rateCards,
      });
    } else if (projectType === 'retainer') {
      onChange({
        ...safeSalesBudget,
        projectType,
        monthlyRetainer,
        includedHours,
        overageRate,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractValue, milestones, estimatedRevenue, rateCards, monthlyRetainer, includedHours, overageRate, projectType]);

  return (
    <div className="space-y-6">
      {/* Fixed Price Project */}
      {projectType === 'fixed-price' && (
        <div className="space-y-4">
          {/* Contract Value */}
          <div className="space-y-2">
            <Label htmlFor="contractValue" className="text-base font-semibold">
              Contract Value
            </Label>
            <div className="flex items-center gap-3">
              <NumberInput
                value={contractValue}
                onChange={setContractValue}
                className="h-11 text-base"
                placeholder="0.00"
              />
              <Button
                type="button"
                variant="outline"
                size="default"
                onClick={() => {
                  if (!contractValue || milestones.length === 0) {
                    return;
                  }
                  
                  const equalAmount = contractValue / milestones.length;
                  const updatedMilestones = milestones.map((m: any) => ({
                    ...m,
                    amount: Math.round(equalAmount * 100) / 100
                  }));
                  
                  setMilestones(updatedMilestones);
                }}
                disabled={!contractValue || milestones.length === 0}
                className="h-11 whitespace-nowrap"
                title="Distribute contract value equally across all milestones"
              >
                <ArrowDownUp className="h-4 w-4 mr-2" />
                Distribute Equally
              </Button>
            </div>
          </div>

          {/* Validation Warning */}
          {hasValidationWarning && (
            <Alert variant="destructive">
              <AlertDescription>
                Payment schedule total (£{formatCurrency(milestonesTotal)}) does not match contract value (£{formatCurrency(contractValue)}). 
                Difference: £{formatCurrency(Math.abs(contractValue - milestonesTotal))}
              </AlertDescription>
            </Alert>
          )}

          {/* Payment Schedule Table */}
          <div className="border rounded-lg">
            <div className="flex items-center justify-between px-4 py-3 border-b bg-muted/50">
              <h3 className="text-base font-semibold">Payment Schedule</h3>
              <div className="flex items-center gap-2">
                <Button
                  onClick={duplicateMilestones}
                  size="sm"
                  variant="outline"
                  disabled={selectedMilestones.size === 0}
                  title={selectedMilestones.size > 1 ? `Duplicate ${selectedMilestones.size} selected milestones` : "Duplicate milestone"}
                >
                  <Files className="h-4 w-4 mr-2" />
                  Duplicate
                </Button>
                <Button
                  onClick={deleteMilestones}
                  size="sm"
                  variant="outline"
                  disabled={selectedMilestones.size === 0}
                  title={selectedMilestones.size > 1 ? `Delete ${selectedMilestones.size} selected milestones` : "Delete milestone"}
                  className="text-destructive hover:text-destructive hover:bg-destructive/10"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </Button>
                <Button onClick={addMilestone} size="sm" variant="outline">
                  <Plus className="h-4 w-4 mr-2" />
                  Add
                </Button>
              </div>
            </div>

            {milestones.length === 0 ? (
              <div className="text-center py-12">
                <div className="flex justify-center mb-4">
                  <div className="rounded-full bg-muted p-3">
                    <Calendar className="h-6 w-6 text-muted-foreground" />
                  </div>
                </div>
                <h3 className="text-lg font-semibold mb-2">No Milestones</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Add payment milestones to track the payment schedule
                </p>
                <Button onClick={addMilestone} variant="outline">
                  <Plus className="h-4 w-4 mr-2" />
                  Add First Milestone
                </Button>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[40px]">
                      <Checkbox
                        checked={selectedMilestones.size === milestones.length && milestones.length > 0}
                        onCheckedChange={toggleAllMilestones}
                        aria-label="Select all milestones"
                      />
                    </TableHead>
                    <TableHead className="w-[60px]"></TableHead>
                    <TableHead className="min-w-[250px]">Name</TableHead>
                    <TableHead className="w-[180px]">Amount</TableHead>
                    <TableHead className="w-[180px]">Due Date</TableHead>
                    <TableHead className="w-[120px] text-right">% of Contract</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {milestones.map((milestone: any, index: number) => {
                    const isSelected = selectedMilestones.has(milestone.id);
                    const percentage = contractValue > 0 ? (milestone.amount / contractValue) * 100 : 0;
                    const isLargeMilestone = percentage > 50;

                    return (
                      <TableRow
                        key={milestone.id}
                        className={cn(
                          'group',
                          isSelected && 'bg-primary/10'
                        )}
                      >
                        <TableCell className="p-2">
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => toggleMilestoneSelection(milestone.id)}
                            aria-label={`Select ${milestone.name || `Milestone ${index + 1}`}`}
                            disabled={milestone.invoiced}
                            title={milestone.invoiced ? "Cannot select - milestone has been invoiced" : undefined}
                          />
                        </TableCell>
                        <TableCell className="p-2">
                          <div className="flex items-center justify-center">
                            <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-primary/10 text-primary text-sm font-semibold">
                              {index + 1}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="p-2">
                          <Input
                            value={milestone.name}
                            onChange={(e) => updateMilestone(milestone.id, 'name', e.target.value)}
                            placeholder={`e.g., Milestone ${index + 1}`}
                            className="h-9"
                            disabled={milestone.invoiced}
                          />
                        </TableCell>
                        <TableCell className="p-2">
                          <NumberInput
                            value={milestone.amount}
                            onChange={(value) => updateMilestone(milestone.id, 'amount', value)}
                            className="h-9"
                            placeholder="0.00"
                            disabled={milestone.invoiced}
                          />
                        </TableCell>
                        <TableCell className="p-2">
                          <Input
                            type="date"
                            value={milestone.dueDate}
                            onChange={(e) => updateMilestone(milestone.id, 'dueDate', e.target.value)}
                            className="h-9"
                            disabled={milestone.invoiced}
                          />
                        </TableCell>
                        <TableCell className="p-2 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <span className={cn(
                              "text-sm font-medium",
                              isLargeMilestone && "text-amber-600"
                            )}>
                              {percentage.toFixed(1)}%
                            </span>
                            {isLargeMilestone && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">
                                Large
                              </span>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
                <TableFooter>
                  <TableRow>
                    <TableCell colSpan={3} className="font-semibold">
                      Total
                    </TableCell>
                    <TableCell className="p-2">
                      <Input
                        value={formatCurrency(milestonesTotal)}
                        readOnly
                        className={cn(
                          "h-9 font-semibold bg-muted cursor-default",
                          hasValidationWarning && "border-destructive text-destructive"
                        )}
                      />
                    </TableCell>
                    <TableCell colSpan={2} className="text-right">
                      <span className={cn(
                        "text-sm font-semibold",
                        hasValidationWarning && "text-destructive"
                      )}>
                        {contractValue > 0 ? ((milestonesTotal / contractValue) * 100).toFixed(1) : '0.0'}% of contract
                      </span>
                    </TableCell>
                  </TableRow>
                </TableFooter>
              </Table>
            )}
          </div>
        </div>
      )}

      {/* Time & Materials Project */}
      {projectType === 'time-and-materials' && (
        <div className="space-y-4">
          {/* Estimated Revenue */}
          <div className="space-y-2">
            <Label htmlFor="estimatedRevenue" className="text-base font-semibold">
              Estimated Revenue
            </Label>
            <p className="text-sm text-muted-foreground">
              Optional: Provide an estimated total revenue for planning purposes
            </p>
            <NumberInput
              value={estimatedRevenue}
              onChange={setEstimatedRevenue}
              className="h-11 text-base"
              placeholder="0.00"
            />
          </div>

          {/* Rate Cards Table */}
          <div className="border rounded-lg">
            <div className="flex items-center justify-between px-4 py-3 border-b bg-muted/50">
              <h3 className="text-base font-semibold">Rate Cards</h3>
              <div className="flex items-center gap-2">
                <Button
                  onClick={duplicateRateCards}
                  size="sm"
                  variant="outline"
                  disabled={selectedRateCards.size === 0}
                  title={selectedRateCards.size > 1 ? `Duplicate ${selectedRateCards.size} selected rate cards` : "Duplicate rate card"}
                >
                  <Files className="h-4 w-4 mr-2" />
                  Duplicate
                </Button>
                <Button
                  onClick={deleteRateCards}
                  size="sm"
                  variant="outline"
                  disabled={selectedRateCards.size === 0}
                  title={selectedRateCards.size > 1 ? `Delete ${selectedRateCards.size} selected rate cards` : "Delete rate card"}
                  className="text-destructive hover:text-destructive hover:bg-destructive/10"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </Button>
                <Button onClick={addRateCard} size="sm" variant="outline">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Rate Card
                </Button>
              </div>
            </div>

            {rateCards.length === 0 ? (
              <div className="text-center py-12">
                <div className="flex justify-center mb-4">
                  <div className="rounded-full bg-muted p-3">
                    <Users className="h-6 w-6 text-muted-foreground" />
                  </div>
                </div>
                <h3 className="text-lg font-semibold mb-2">No Rate Cards</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Add rate cards to define billing rates for different roles
                </p>
                <Button onClick={addRateCard} variant="outline">
                  <Plus className="h-4 w-4 mr-2" />
                  Add First Rate Card
                </Button>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[40px]">
                      <Checkbox
                        checked={selectedRateCards.size === rateCards.length && rateCards.length > 0}
                        onCheckedChange={toggleAllRateCards}
                        aria-label="Select all rate cards"
                      />
                    </TableHead>
                    <TableHead className="w-[60px]"></TableHead>
                    <TableHead className="min-w-[200px]">Role</TableHead>
                    <TableHead className="w-[150px]">Bill Rate (per hour)</TableHead>
                    <TableHead className="w-[150px]">Estimated Hours</TableHead>
                    <TableHead className="w-[150px] text-right">Estimated Revenue</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rateCards.map((rateCard: any, index: number) => {
                    const billRate = getRoleBillRate(rateCard.role, rateCard.billRate);
                    const estimatedRevenue = billRate * rateCard.estimatedHours;
                    const isSelected = selectedRateCards.has(rateCard.id);

                    return (
                      <TableRow
                        key={rateCard.id}
                        className={cn(
                          'group',
                          isSelected && 'bg-primary/10'
                        )}
                      >
                        <TableCell className="p-2">
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => toggleRateCardSelection(rateCard.id)}
                            aria-label={`Select ${rateCard.role || `Rate Card ${index + 1}`}`}
                          />
                        </TableCell>
                        <TableCell className="p-2">
                          <div className="flex items-center justify-center">
                            <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-primary/10 text-primary text-sm font-semibold">
                              {index + 1}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="p-2">
                          <Input
                            value={rateCard.role}
                            onChange={(e) => updateRateCard(rateCard.id, 'role', e.target.value)}
                            placeholder="e.g., Senior Developer"
                            className="h-9"
                          />
                        </TableCell>
                        <TableCell className="p-2">
                          <NumberInput
                            value={billRate}
                            onChange={(value) => updateRateCard(rateCard.id, 'billRate', value)}
                            className="h-9"
                            placeholder="0.00"
                          />
                        </TableCell>
                        <TableCell className="p-2">
                          <NumberInput
                            value={rateCard.estimatedHours}
                            onChange={(value) => updateRateCard(rateCard.id, 'estimatedHours', value)}
                            className="h-9"
                            placeholder="0.00"
                          />
                        </TableCell>
                        <TableCell className="p-2 text-right">
                          <span className="text-sm font-medium">
                            £{formatCurrency(estimatedRevenue)}
                          </span>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
                <TableFooter>
                  <TableRow>
                    <TableCell colSpan={4} className="font-semibold">
                      Total ({rateCards.length} rate card{rateCards.length !== 1 ? 's' : ''})
                    </TableCell>
                    <TableCell className="p-2">
                      <Input
                        value={formatCurrency(totalEstimatedHours)}
                        readOnly
                        className="h-9 font-semibold bg-muted cursor-default"
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <span className="text-base font-semibold">
                        £{formatCurrency(rateCardsTotal)}
                      </span>
                    </TableCell>
                  </TableRow>
                </TableFooter>
              </Table>
            )}
          </div>
        </div>
      )}

      {/* Retainer Project */}
      {projectType === 'retainer' && (
        <div className="space-y-4">
          <div className="border rounded-lg p-6 space-y-6">
            <div>
              <h3 className="text-base font-semibold mb-4">Retainer Details</h3>
              <div className="grid gap-6">
                {/* Monthly Retainer */}
                <div className="space-y-2">
                  <Label htmlFor="monthlyRetainer" className="text-sm font-medium">
                    Monthly Retainer Amount
                  </Label>
                  <NumberInput
                    value={monthlyRetainer}
                    onChange={setMonthlyRetainer}
                    className="h-10"
                    placeholder="0.00"
                  />
                </div>

                {/* Included Hours */}
                <div className="space-y-2">
                  <Label htmlFor="includedHours" className="text-sm font-medium">
                    Included Hours per Month
                  </Label>
                  <NumberInput
                    value={includedHours}
                    onChange={setIncludedHours}
                    className="h-10"
                    placeholder="0.00"
                  />
                </div>

                {/* Overage Rate */}
                <div className="space-y-2">
                  <Label htmlFor="overageRate" className="text-sm font-medium">
                    Overage Rate (per hour)
                  </Label>
                  <NumberInput
                    value={overageRate}
                    onChange={setOverageRate}
                    className="h-10"
                    placeholder="0.00"
                  />
                </div>
              </div>
            </div>

            {/* Summary */}
            <div className="pt-4 border-t">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Effective hourly rate:</span>
                <span className="font-semibold">
                  £{includedHours > 0 ? formatCurrency(monthlyRetainer / includedHours) : '0.00'} per hour
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Validation Dialog */}
      <AlertDialog open={showDeleteValidationDialog} onOpenChange={setShowDeleteValidationDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cannot Delete Invoiced Milestones</AlertDialogTitle>
            <AlertDialogDescription>
              The following milestone(s) cannot be deleted because they have invoices created against them:
              <div className="mt-3 p-3 bg-muted rounded-md">
                <p className="font-medium text-foreground">{invoicedMilestoneNames}</p>
              </div>
              <p className="mt-3">
                Please deselect these milestones and try again.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setShowDeleteValidationDialog(false)}>
              OK
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </div>
  );
}
